import CentricDispatcher from '../dispatcher/CentricDispatcher.react';
import { ActionTypes } from '../constants/CentricConstants';
import { ReduceStore } from 'flux/utils';
import update from 'immutability-helper';

const getEmptyState = () => ({isFetching: 0, fetchedAll: false, attendees: []});
const sanitise = ({id, userId, eventId, userDeleted, ...rest}) => ({
    id: Number(id),
    userId: Number(userId),
    eventId: Number(eventId),
    userDeleted: !!Number(userDeleted),
    ...rest
});

class EventAttendeeStore extends ReduceStore {

    getInitialState() {
        return getEmptyState();
    }

    getIndex({id, userId, eventId}) {
        let index = -1;
        if (!!id) {
            index = this._state.attendees.findIndex(att => (att && att.id === id));
        }
        if (index === -1 && !!userId && !!eventId) {
            index = this._state.attendees.findIndex(att => (att && att.eventId === eventId && att.userId === userId));
        }
        return index;
    }

    isFetching() {
        return this.getState().isFetching > 0;
    }

    fetchedAll() {
        return this.getState().fetchedAll;
    }

    getDetails(eventId, userId) {
        return this.getState().attendees.find(attendance => (attendance && (attendance.eventId === eventId) && (attendance.userId === userId)));
    }

    getEventGuests(eventId) {
        return this.getState().attendees.filter(attendance => attendance && attendance.eventId === eventId);
    }

    getByEvent(eventId) {
        return this.getState().attendees.filter(attendance => attendance.eventId === eventId && attendance.role !== 'trainer');
    }

    getTrainersByEvent(eventId) {
        return this.getState().attendees.filter(attendance => attendance.eventId === eventId && attendance.role === 'trainer');
    }

    reduce(state, action) {
        let itemIndex, data, idList;
        switch (action.type) {
            case ActionTypes.EVENT_ATTENDEE_LIST_REQUEST:
                return update(this.getState(), { isFetching: { $set: (state.isFetching + 1) }});

            case ActionTypes.EVENT_ATTENDEE_LIST_SUCCESS:
                data = action.data.map(sanitise);
                return {
                    isFetching: (state.isFetching - 1),
                    fetchedAll: data.length === 0,
                    attendees: [
                        ...this.getState().attendees.filter(this.getFilter(action.payload)),
                        ...action.data.map(sanitise)
                    ]
                };

            case ActionTypes.EVENT_ATTENDEE_LIST_FAILURE:
                return update(this.getState(), { isFetching: { $set: (state.isFetching - 1) }});


            case ActionTypes.EVENT_ATTENDEE_INFO_REQUEST:
                return update(this.getState(), { isFetching: { $set: (state.isFetching + 1) }});

            case ActionTypes.EVENT_ATTENDEE_INFO_SUCCESS:
                data = sanitise(action.data);
                itemIndex = this.getIndex(data);
                return update(this.getState(), {
                    isFetching: { $set: (state.isFetching - 1) },
                    attendees: (0 <= itemIndex
                        ? { [itemIndex]: { $set: data }}
                        : { $push: [ data ]}
                    )
                });

            case ActionTypes.EVENT_ATTENDEE_INFO_FAILURE:
                return update(this.getState(), { isFetching: { $set: (state.isFetching - 1) }});

            case ActionTypes.EVENT_ATTENDEE_ADD_REQUEST:
                if (!action.payload.draft) { return update(this.getState(), {isFetching: { $set: (state.isFetching + 1) } }); }

                const draft = {
                    eventId: action.payload.eventId,
                    ...action.payload.draft
                };
                return update(this.getState(), {
                    isFetching: { $set: (state.isFetching + 1) },
                    attendees: { $push: [ draft ] }
                });

            case ActionTypes.EVENT_ATTENDEE_ADD_SUCCESS:
                data = sanitise(action.data);
                itemIndex = this.getIndex(data);
                return update(this.getState(), {
                    isFetching: { $set: (state.isFetching - 1) },
                    attendees: { [itemIndex]: { $set: data } }
                });

            case ActionTypes.EVENT_ATTENDEE_ADD_FAILURE:
                if (!action.payload.draft) { return update(this.getState(), {isFetching: { $set: (state.isFetching - 1) } }); }
                itemIndex = this.getIndex(action.payload.draft);
                return update(this.getState(), {
                    isFetching: { $set: (state.isFetching - 1) },
                    attendees: { $splice: [[itemIndex, 1]] }
                });

            case ActionTypes.EVENT_ATTENDEE_SAVE_REQUEST:
                itemIndex = this.getIndex(action.payload.current);
                return update(this.getState(), {
                    isFetching: { $set: (state.isFetching + 1) },
                    attendees: { [itemIndex]: { $set: action.payload.newValue } }
                });

            case ActionTypes.EVENT_ATTENDEE_SAVE_SUCCESS:
                itemIndex = this.getIndex(action.payload.current);
                return update(this.getState(), {
                    isFetching: { $set: (state.isFetching - 1) },
                    attendees: { [itemIndex]: { $set: sanitise(action.data) } }
                });

            case ActionTypes.EVENT_ATTENDEE_SAVE_FAILURE:
                itemIndex = this.getIndex(action.payload.newValue);
                return update(this.getState(), {
                    isFetching: { $set: (state.isFetching - 1) },
                    attendees: { [itemIndex]: { $set: action.payload.current } }
                });

            case ActionTypes.EVENT_ATTENDEE_DELETE_REQUEST:
                if (!action.payload.current) { return update(this.getState(), { isFetching: { $set: (state.isFetching + 1) } }); }
                if (!action.payload.current && !!action.payload.eventId) {
                    action.payload.current.eventId = action.payload.eventId;
                }
                itemIndex = this.getIndex(action.payload.current);
                if (0 > itemIndex) { return update(this.getState(), { isFetching: { $set: (state.isFetching + 1) } }); }
                return update(this.getState(), {
                    isFetching: { $set: (state.isFetching + 1) },
                    attendees: { [itemIndex]: { status: { $set: 'removing...' } } }
                });

            case ActionTypes.EVENT_ATTENDEE_DELETE_SUCCESS:
                if (!action.payload.current) { return update(this.getState(), { isFetching: { $set: (state.isFetching - 1) } }); }
                if (!action.payload.current && !!action.payload.eventId) {
                    action.payload.current.eventId = action.payload.eventId;
                }
                itemIndex = this.getIndex(action.payload.current);
                if (0 > itemIndex) { return update(this.getState(), { isFetching: { $set: (state.isFetching + 1) } }); }
                return update(this.getState(), {
                    isFetching: { $set: (state.isFetching - 1) },
                    attendees: { $splice: [[itemIndex, 1]] }
                });

            case ActionTypes.EVENT_ATTENDEE_DELETE_FAILURE:
                if (!action.payload.current) { return update(this.getState(), { isFetching: { $set: (state.isFetching - 1) } }); }
                if (!action.payload.current && !!action.payload.eventId) {
                    action.payload.current.eventId = action.payload.eventId;
                }
                itemIndex = this.getIndex(action.payload.current);
                if (0 > itemIndex) { return update(this.getState(), { isFetching: { $set: (state.isFetching + 1) } }); }
                return update(this.getState(), {
                    isFetching: { $set: (state.isFetching - 1) },
                    attendees: { [itemIndex]: { $set: action.payload.current } }
                });

            default:
                return state;
        }
    }

    getFilter({id, eventId, userId, role, options}) {
        if (!id && !eventId && !userId && !role) { return () => false; }
        if (id) { return (e) => e && e.id !== id; }
        if (!!options && !!options.page) { return (e) => !!e; }
        if (eventId || userId || role) { return (e) => e && e.eventId !== eventId && e.userId !== userId && e.role !== role; }
        return (e) => !!e;
    }
}

export default new EventAttendeeStore(CentricDispatcher);
